// @flow
import React from 'react';
import type { Node } from 'react';
import moment from 'moment/moment';
import { Drawer } from '@zola/zola-ui/src/components/Drawer';

import './RegistryHeader.less';

type RegistryInfo = {
  name: string,
  greeting: string,
  event_date: string,
  hero_image: string,
  profile_image: string,
};

type Props = {
  registryInfo: RegistryInfo,
};

/**
 * Determine whether custom profile image should be shown
 * @param profileImage
 * @returns {boolean}
 */
const showProfilePicture = (profileImage: string): boolean => {
  switch (profileImage) {
    case '/assets/images/couple-mf.png':
    case '/assets/images/couple-ff.png':
    case '/assets/images/couple-mm.png':
      return false;
    default:
      return true;
  }
};

/**
 * helper function for get event date information text. Variations includes,
 * 1.) (Date coming soon)
 * 2.) January 1, 2019 (Today)
 * 3.) January 1, 2019 (1 day left)
 * 4.) January 1, 2019 (x day left), x being a number
 * @param eventDate
 * @returns {string}
 */
const getEventDateMessage = (eventDate: ?string): string => {
  if (!eventDate) {
    return 'Date coming soon';
  }

  const targetDate = moment.utc(eventDate);
  const timezoneOffset = new Date().getTimezoneOffset() / 60;
  const hoursDiff = moment.utc().diff(targetDate, 'hours') - timezoneOffset;
  const daysToWedding = -Math.floor(hoursDiff / 24);
  const daysToWeddingText =
    daysToWedding === 1 ? ` (${daysToWedding} day left)` : ` (${daysToWedding} days left)`;
  const dayLeftText = daysToWedding === 0 ? '(Today)' : daysToWeddingText;
  const formattedDateMessage = targetDate.format('MMMM DD, YYYY');
  const eventDateMessage =
    daysToWedding < 0 ? `${formattedDateMessage}` : `${formattedDateMessage} ${dayLeftText}`;

  return eventDateMessage;
};

/**
 * Render Greeting message. Use a drawer to display long messages
 * @param greeting
 * @returns {null|*}
 */
const renderGreetingMessage = (greeting: ?string): Node | null => {
  if (!greeting) {
    return null;
  }
  const content = (
    <div className="greeting-content">
      <h3 className="greeting-text">{greeting}</h3>
    </div>
  );
  if (greeting.split('\n').length > 3 || greeting.length > 300) {
    return (
      <Drawer skipScrollToTop maxHeight="60px" buttonText={{ open: 'Read More', close: 'Close' }}>
        {content}
      </Drawer>
    );
  }

  return content;
};

const RegistryHeader = ({ registryInfo }: Props) => {
  const {
    name,
    greeting,
    event_date: eventDate,
    hero_image: heroImage,
    profile_image: profileImage,
  } = registryInfo;
  const divStyle = {
    backgroundImage: `url(${heroImage})`,
  };

  const eventDateMessage: string = getEventDateMessage(eventDate);

  const profileImageUrl: ?string = showProfilePicture(profileImage) ? profileImage : null;

  return (
    <section className="registry-header">
      <div className="registry-header_hero-image" style={divStyle}>
        <div className="registry-header_hero-gradient" aria-hidden="true" />
        <div className="container registry-header_content">
          <div className="row">
            <div className="col-xs-12">
              <h1 className="registry-header_title text-center hidden-xs">{name}</h1>
              <h2 className="registry-header_registry-date hidden-xs">{eventDateMessage}</h2>
            </div>
          </div>
        </div>
        {profileImageUrl && (
          <img
            className="registry-header_couple-img-circle img-responsive"
            src={profileImageUrl}
            alt={eventDateMessage}
          />
        )}
      </div>
      <div className="container registry-header_greeting text-center">
        <h2 className="registry-title visible-xs overflow-hidden">
          <strong>{name}</strong>
        </h2>
        <h4 className="registry-date visible-xs text-muted">{eventDateMessage}</h4>
        {renderGreetingMessage(greeting)}
      </div>
    </section>
  );
};

export default React.memo<Props>(RegistryHeader);
